import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { X as XIcon, Calendar, Stethoscope, Eye, Pill, Activity, FileText, ShieldCheck, Loader2, Printer, FileDown, Tag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Drawer/modal read-only per visualizzare l'anteprima di una visita dallo storico paziente.
 * Apre rapidamente i dati salienti senza navigare alla pagina cartella completa.
 *
 * Props:
 *  - visitId: id della visita da mostrare
 *  - open: bool
 *  - onClose: () => void
 *  - onOpenFull?: (vid) => void — opzionale, naviga alla cartella completa
 *  - allowActions?: bool — se true mostra pulsanti PDF/Stampa/Invia (solo per visite chiuse)
 */
export default function VisitPreviewDrawer({ visitId, open, onClose, onOpenFull, allowActions = true }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !visitId) { setV(null); setError(null); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get(`/visits/${visitId}`);
        if (alive) setV(data);
      } catch (e) {
        if (alive) {
          const status = e?.response?.status;
          if (status === 403) setError("Visita non accessibile: appartiene a un altro medico.");
          else if (status === 404) setError("Visita non trovata.");
          else setError(apiErr(e, "Errore caricamento visita"));
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [visitId, open]);

  const openPdf = async (forPrint = false) => {
    if (!v) return;
    try {
      const { data } = await api.get(`/visits/${v.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (forPrint && w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch {} });
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e, "Errore PDF")); }
  };

  if (!open) return null;

  const eye = (e) => e || {};
  const OD = eye(v?.OD);
  const OS = eye(v?.OS);
  const isClosed = v?.status === "closed";
  const dateLabel = (v?.closed_at || v?.created_at || "").slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="visit-preview-drawer-backdrop">
      <div
        className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right"
        onClick={(e) => e.stopPropagation()}
        data-testid="visit-preview-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-brand-navy text-white px-5 py-4 flex items-center justify-between border-b border-brand-navyDark">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={18} className="shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Anteprima visita</div>
              <div className="font-extrabold text-base truncate" data-testid="preview-patient-name">
                {v ? `${v.patient_name || ""} ${v.patient_surname || ""}`.trim() || "—" : "—"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/10 p-1.5 rounded-lg" data-testid="preview-close">
            <XIcon size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {loading && (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="animate-spin inline mr-2" size={16} /> Caricamento anteprima…
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900 flex items-start gap-2" data-testid="preview-error">
              <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {v && !loading && !error && (
            <>
              {/* Read-only badge */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold flex items-center gap-1.5" data-testid="preview-readonly-badge">
                  <ShieldCheck size={11} /> Anteprima sola lettura
                </span>
                {isClosed
                  ? <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">Visita chiusa</span>
                  : <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold">Bozza</span>}
                <span className="text-[11px] text-slate-500 ml-auto inline-flex items-center gap-1">
                  <Calendar size={11} /> <span data-testid="preview-date">{dateLabel}</span>
                </span>
              </div>

              {/* Header info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Tipo visita" value={(v.service || "—").replace(/_/g, " ").toUpperCase()} testid="preview-service" />
                <Info label="Stato" value={isClosed ? "Chiusa" : "In bozza"} testid="preview-status" />
                <Info label="Data nascita" value={v.patient_birth || "—"} />
                <Info label="Medico" value={v.doctor_name || "—"} />
              </div>

              {/* Tags / hashtag */}
              {Array.isArray(v.tags) && v.tags.length > 0 && (
                <div data-testid="preview-tags">
                  <Label icon={Tag}>Tag paziente</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {v.tags.map((t) => (
                      <span key={t} className="px-2 py-0.5 rounded-full bg-brand-navy text-white text-[11px] font-bold">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sintesi clinica top: visus / tono OD-OS */}
              <Section title="Visus e Tono (OD / OS)" testid="preview-clinical-top">
                <div className="grid grid-cols-2 gap-3">
                  <ClinicalCol side="OD" eye={OD} />
                  <ClinicalCol side="OS" eye={OS} />
                </div>
              </Section>

              {/* Diagnosi */}
              {v.diagnosi && (
                <Section title="Diagnosi" testid="preview-diagnosi" icon={Activity}>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{v.diagnosi}</p>
                </Section>
              )}

              {/* Terapia: libera + items */}
              {(v.terapia || (Array.isArray(v.terapia_items) && v.terapia_items.length > 0)) && (
                <Section title="Terapia" testid="preview-terapia" icon={Pill}>
                  {v.terapia && <p className="text-sm text-slate-800 whitespace-pre-wrap mb-2">{v.terapia}</p>}
                  {Array.isArray(v.terapia_items) && v.terapia_items.length > 0 && (
                    <ul className="text-sm space-y-1" data-testid="preview-terapia-items">
                      {v.terapia_items.map((it, i) => (
                        <li key={`${it.brand || it.principle || "item"}-${i}`} className="border-l-2 border-brand-azure pl-2">
                          <b>{it.brand || it.principle}</b>{it.principle && it.brand ? <span className="text-slate-500"> ({it.principle})</span> : null}
                          {it.dose && <span className="text-slate-600"> — {it.dose}</span>}
                          {it.frequency_per_day && <span className="text-slate-600"> · {it.frequency_per_day}/die</span>}
                          {it.days && <span className="text-slate-600"> · {it.days} gg</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              )}

              {/* Protocollo suggerito */}
              {v.behavioral_protocol && (
                <Section title="Protocollo suggerito" testid="preview-protocollo" icon={Stethoscope}>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{v.behavioral_protocol}</p>
                </Section>
              )}

              {/* Anamnesi */}
              {v.anamnesi && (
                <Section title="Anamnesi" testid="preview-anamnesi">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{v.anamnesi}</p>
                </Section>
              )}

              {/* Referto */}
              {v.referto && (
                <Section title="Referto" testid="preview-referto" icon={FileText}>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{v.referto}</p>
                </Section>
              )}

              {/* Addendums */}
              {Array.isArray(v.addendums) && v.addendums.length > 0 && (
                <Section title={`Addendum (${v.addendums.length})`} testid="preview-addendums">
                  <ul className="space-y-2">
                    {v.addendums.map((a) => (
                      <li key={a.id} className="border-l-4 border-brand-azure pl-3 py-1 text-sm">
                        <div className="text-[10px] text-slate-500">{(a.at || "").slice(0, 16)} · {a.author}</div>
                        <div className="text-slate-800 whitespace-pre-wrap">{a.text}</div>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Privacy footer */}
              <div className="text-[10px] text-slate-400 italic flex items-start gap-1.5 pt-2 border-t border-slate-100">
                <ShieldCheck size={10} className="mt-0.5" />
                <span>Le note personali del medico NON sono mostrate in anteprima e non vengono incluse nel PDF/referto pubblico.</span>
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        {v && !loading && !error && (
          <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-5 py-3 flex flex-wrap justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="preview-cancel">
              Chiudi
            </button>
            {onOpenFull && (
              <button
                onClick={() => onOpenFull(v.id)}
                className="px-4 py-2 text-sm font-bold text-brand-azure border border-brand-azure hover:bg-brand-azure hover:text-white rounded-lg"
                data-testid="preview-open-full"
              >
                Apri cartella completa
              </button>
            )}
            {allowActions && isClosed && (
              <>
                <button
                  onClick={() => openPdf(false)}
                  className="px-4 py-2 text-sm font-bold bg-brand-navy hover:bg-brand-navyDark text-white rounded-lg flex items-center gap-1.5"
                  data-testid="preview-pdf"
                >
                  <FileDown size={13} /> PDF
                </button>
                <button
                  onClick={() => openPdf(true)}
                  className="px-4 py-2 text-sm font-bold bg-brand-azure hover:bg-brand-azureDark text-white rounded-lg flex items-center gap-1.5"
                  data-testid="preview-print"
                >
                  <Printer size={13} /> Stampa
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-3" data-testid={testid}>
      <Label icon={Icon}>{title}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
      {Icon && <Icon size={11} />}{children}
    </div>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-brand-navy font-semibold" data-testid={testid}>{value}</div>
    </div>
  );
}

function _formatIop(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (s.toLowerCase().includes("mmhg")) return s;
  return `${s} mmHg`;
}

function ClinicalCol({ side, eye }) {
  const rows = [
    { k: "visus_naturale", label: "Visus naturale" },
    { k: "visus_corretto", label: "Visus corretto" },
    { k: "tono", label: "Tono", suffix: eye.tono_metodo ? ` (${eye.tono_metodo})` : "", fmt: _formatIop },
    { k: "annessi_palpebre", label: "Annessi" },
    { k: "fundus", label: "Fundus" },
  ];
  const hasData = rows.some((r) => eye[r.k]);
  return (
    <div className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`preview-eye-${side.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded flex items-center justify-center text-white font-bold text-xs ${side === "OD" ? "bg-brand-navy" : "bg-brand-azure"}`}>{side}</div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1"><Eye size={10} /> {side === "OD" ? "Destro" : "Sinistro"}</span>
      </div>
      {hasData ? (
        <ul className="space-y-1">
          {rows.map((r) => eye[r.k] ? (
            <li key={r.k} className="text-xs">
              <span className="text-slate-500">{r.label}{r.suffix}:</span>{" "}
              <span className="text-brand-navy font-semibold break-words" data-testid={`preview-eye-${side.toLowerCase()}-${r.k}`}>{r.fmt ? r.fmt(eye[r.k]) : String(eye[r.k])}</span>
            </li>
          ) : null)}
        </ul>
      ) : (
        <div className="text-[11px] text-slate-400 italic">Nessun dato registrato</div>
      )}
    </div>
  );
}
